import React, {useEffect, useMemo, useState} from 'react';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import AdmicityStatus from '../../../../../shared-components/AdmicityStatus';
import {
    useGetFormsByStudentQuery,
    useGetCommunicationFormsByStudentQuery,
    useGetStudentNotificationFormsQuery,
    useLazyGetStudentNotificationFormResponsesQuery
} from '../../../../../api/services/formRequestsService';
import AdmicityTableV2 from '../../../../../shared-components/Table/V2/AdmicityTableV2';
import {formatDate, formatFormExpirationDate} from '../../../../../utility/dateUtil';
import withPaginationV2 from '../../../../../hoc/pagination/withPaginationV2';
import {getFormRequestStatusInfo} from '../../../RequestAndResponses/common/common';
import FILTER_OPERATIONS from '../../../../../constants/filterOperations';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../../../constants/formRequestStatuses';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByStudentQuery,
} from '../../../../../api/services/filesService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import useNotification from '../../../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../../../shared-components/AdmicityTabs';
import {Box, IconButton, Paper, Tooltip, useMediaQuery, useTheme} from '@mui/material';
import NotificationFormsTable from '../../../../../shared-components/NotificationFormsTable';
import {getApprovalStatusInfo} from '../../../../../constants/FormApprovalStatuses';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const expirationHeader = (
    <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5
    }}>
        <span>Expiration Date</span>
        <Tooltip title="Expiration date and time are shown in your local time">
            <IconButton
                size="small"
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InfoOutlinedIcon fontSize="inherit"/>
            </IconButton>
        </Tooltip>
    </Box>
);

/* eslint-disable react/prop-types */
const formsTableColumns = [
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())}/>,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        header: expirationHeader,
        Cell: ({cell}) => cell.getValue() ? formatFormExpirationDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
    {
        id: 'editedBy',
        accessorFn: (row) => row?.editedBy?.fullname ?? '',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        Cell: ({row}) => {
            return row.original?.approvalStage ?? '';
        },
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => {
            const value = getValue();
            if (!value) return '';
            return <AdmicityStatus {...getApprovalStatusInfo(value)} />;
        },
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
];

const generalFormsTableColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    }
];

const communicationFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'sentAt',
        header: 'Date',
        Cell: ({cell}) => {
            const date = cell.getValue();
            return date ? formatDate(date) : '';
        },
        enableColumnFilter: false,
    },
];

/* eslint-enable react/prop-types */
const StudentForms = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const downloadFile = useFileDownloader();
    const {showSuccessNotification} = useNotification();
    const navigate = useNavigate();
    const {studentId} = useParams();
    const studentIdNumber = Number(studentId);
    const outletContext = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(outletContext.formsState?.currentTabIndex ?? 0);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(outletContext.formsState?.selectedNotificationFormIndex);
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetStudentNotificationFormsQuery(studentId, {skip: currentTabIndex !== 1});
    const [generateFormResponsesReportByStudent] = useLazyGenerateFormResponsesReportByStudentQuery();
    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByStudentQuery,
        props: {studentId}
    }), [studentId]);
    const CommunicationFormsTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetCommunicationFormsByStudentQuery,
        props: {studentId}
    }), [studentId]);

    useEffect(() => {
        return () => {
            outletContext.setFormsState({currentTabIndex, selectedNotificationFormIndex});
        };
    }, [outletContext.setFormsState, currentTabIndex, selectedNotificationFormIndex]);

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            await generateFormResponsesReportByStudent({formId, studentId});
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const tabs =
        [
            {
                label: 'Forms',
                component: <Table
                    variant="elevation"
                    id={outletContext?.key}
                    columns={formsTableColumns}
                    tableProps={{
                        enableSorting: false,
                        enableFullScreenToggle: false,
                        enableStickyFooter: false,
                        muiTablePaperProps: {
                            sx: {
                                display: 'block',
                                flexGrow: 0,
                                height: 'auto',
                            }
                        },
                        muiTableContainerProps: {
                            sx: {
                                flex: 'unset',
                            }
                        }
                    }}
                    toolbarIconActions={
                        [{
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }]
                    }
                    onRowClick={(params) => navigate(`${params.id}`)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                    rowActions={[
                        {
                            label: 'Download PDF',
                            action: params => downloadPdfReport(params.id, params.form.id, false),
                            disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted
                        }
                    ]}
                />
            },
            {
                label: 'Self-service Forms',
                component:
                    <NotificationFormsTable
                        id={`${outletContext?.key}/general`}
                        data={notificationForms}
                        columns={generalFormsTableColumns}
                        isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                        selectedRowIndex={selectedNotificationFormIndex}
                        setSelectedRowIndex={setSelectedNotificationFormIndex}
                        navigate={navigate}
                        personId={studentIdNumber}
                        fetchResponsesFn={useLazyGetStudentNotificationFormResponsesQuery}
                    />
            },
            {
                label: 'Communications',
                component: <CommunicationFormsTable
                    variant="elevation"
                    id={`${outletContext?.key}/communication`}
                    columns={communicationFormsTableColumns}
                    tableProps={{
                        enableSorting: false,
                        enableColumnResizing: false,
                        enableTopToolbar: false,
                    }}
                    onRowClick={(params) => navigate(`${params.requestId}`)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No communication yet'
                    }}
                />
            },
        ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    ? {}
                    : {
                        minHeight: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                displayDivider
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </Paper>

    );
};

export default StudentForms;
